import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/authStore'
import { useTenantStore } from '@/store/tenantStore'
import { usePageStore } from '@/store/pageStore'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useViewportWidth } from '@/hooks/useViewportWidth'
import { getTenantLogoUrl, getTenantId } from '@/utils/tenant'
import { hasLcPermission, isLcOwner } from '@/utils/roleUtils'
import {
  LayoutDashboard,
  LayoutPanelLeft,
  Users,
  LogOut,
  ChevronRight,
  ChevronDown,
  Shield,
  PanelLeftClose,
  PanelLeft,
  StickyNote,
  ExternalLink,
  Monitor,
  Megaphone,
  UserCheck,
  FileText,
  MessageSquare,
  Building2,
  UserCog,
  Settings,
  Receipt,
  ShoppingBag,
  CalendarRange,
  Library,
  Menu,
} from 'lucide-react'
import { clsx } from 'clsx'
import { Button } from '@quebon/ui/components/button'
import { Dialog, DialogContent, DialogHeader, DialogOverlay, DialogPortal, DialogTitle } from '@quebon/ui/components/dialog'
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'
import { PreviewBadge } from '@/components/wireframe/PreviewBadge'
import { HandheldCoachingShortcut } from '@/components/admin/coaching/handheld/HandheldCoachingShortcut'
import { HandheldCoachingSelectionContext } from '@/components/admin/coaching/handheld/HandheldCoachingSelectionContext'
import { requestCoachingExit, type HandheldSelection } from '@/components/admin/coaching/handheld/handheldNavigation'

// LC 전용 사이드바 메뉴 정의
interface SidebarItem {
  path?: string                // children 있는 그룹은 path 없음
  key?: string                 // 그룹 식별자 (children 있는 항목 필수)
  label: string
  icon: React.ComponentType<{ className?: string }>
  exact?: boolean
  preview?: boolean
  /** Override base path for active-state matching (used when `path` includes a concrete id but the menu should highlight for any sibling route). */
  activeBase?: string
  permission?: string   // LcAdminPermission 키 (없으면 전원 노출)
  anyPermission?: string[] // 하나라도 보유 시 노출
  ownerOnly?: boolean   // owner 전용 메뉴
  devOnly?: boolean     // 개발 환경(localhost)에서만 노출
  mallOnly?: boolean    // 기관의 깨봉몰 운영 설정이 켜진 경우만 노출
  testCenterOnly?: boolean // 운영 모드가 TEST인 센터에서만 노출(시범 화면)
  children?: SidebarItem[] // 아코디언 하위 메뉴
}

// Super Admin 링크와 동일한 조건: Vite dev 또는 localhost 호스트
const isDevEnv = import.meta.env.DEV || window.location.hostname === 'localhost'
const isCoachingRoute = (pathname: string) => pathname === '/admin/coaching' || pathname.startsWith('/admin/coaching/')
const isNewCoachingRoute = (pathname: string) => pathname === '/admin/coaching-new' || pathname.startsWith('/admin/coaching-new/')
const isAltCoachingRoute = (pathname: string) => pathname === '/admin/coaching-alt' || pathname.startsWith('/admin/coaching-alt/')
const isMallRoute = (pathname: string) => pathname === '/admin/mall' || pathname.startsWith('/admin/mall/')
const prefersCollapsedSidebar = (pathname: string) =>
  isCoachingRoute(pathname) || isNewCoachingRoute(pathname) || isMallRoute(pathname)

export default function AdminLayout() {
  const { user, logout } = useAuthStore()
  const { tenantInfo } = useTenantStore()
  const { pageSubtitle } = usePageStore()
  const { t } = useTranslation('nav')
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => prefersCollapsedSidebar(location.pathname))
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false)
  const [handheldSelection, setHandheldSelection] = useState<HandheldSelection | null>(null)
  const legacyCoachingRoute = isCoachingRoute(location.pathname)
  const newCoachingRoute = isNewCoachingRoute(location.pathname)
  const altCoachingRoute = isAltCoachingRoute(location.pathname)
  const coachingRoute = newCoachingRoute || altCoachingRoute
  const viewportWidth = useViewportWidth()
  const mobileSidebarOpen = newCoachingRoute && viewportWidth < 1024 && !sidebarCollapsed
  const sidebarToggleRef = useRef<HTMLButtonElement | null>(null)
  const mobileSidebarToggleRef = useRef<HTMLButtonElement | null>(null)
  const showGradingShortcut = hasLcPermission(user ?? undefined, 'COACHING') && !newCoachingRoute
  const shortcutSpacing: CSSProperties & { '--admin-mobile-grading-clearance': string } = {
    '--admin-mobile-grading-clearance': showGradingShortcut ? 'calc(5.5rem + env(safe-area-inset-bottom))' : '0px',
  }

  const sidebarItems = useMemo<SidebarItem[]>(
    () => [
      { path: '/admin', label: t('adminMenu.dashboard'), icon: LayoutDashboard, exact: true, permission: 'DASHBOARD' },
      { path: '/admin/coaching', label: t('adminMenu.coaching'), icon: UserCheck, permission: 'COACHING' },
      { path: '/admin/coaching-new', label: t('adminMenu.coachingNew'), icon: LayoutPanelLeft, permission: 'COACHING', testCenterOnly: true },
      { path: '/admin/classes', label: t('adminMenu.classManagement'), icon: CalendarRange, permission: 'CLASS' },
      { path: '/admin/members', label: t('adminMenu.studentManagement'), icon: Users, permission: 'STUDENT' },
      { path: '/admin/contents', label: t('adminMenu.contentManagement'), icon: FileText, permission: 'CONTENT' },
      { path: '/admin/orders', label: t('adminMenu.orders'), icon: Receipt, permission: 'PERFORMANCE' },
      { path: '/admin/mall', label: t('adminMenu.mall'), icon: ShoppingBag, permission: 'MALL', mallOnly: true },
      { path: '/admin/announcements', label: t('adminMenu.announcements'), icon: Megaphone, permission: 'ANNOUNCEMENT' },
      { path: '/admin/inquiries', label: t('adminMenu.inquiries'), icon: MessageSquare, permission: 'INQUIRY' },
      { path: '/admin/library', label: t('adminMenu.library'), icon: Library, permission: 'LIBRARY' },
      {
        key: 'settings',
        label: t('adminMenu.settings'),
        icon: Settings,
        children: [
          { path: '/admin/devices', label: t('adminMenu.deviceManagement'), icon: Monitor, permission: 'DEVICE' },
          { path: '/admin/settings/center', label: t('adminMenu.centerInfo'), icon: Building2, permission: 'CENTER_INFO' },
          { path: '/admin/settings/accounts', label: t('adminMenu.centerAccounts'), icon: UserCog, ownerOnly: true },
          { path: '/admin/notes', label: t('adminMenu.notes'), icon: StickyNote, permission: 'DASHBOARD', devOnly: true },
        ],
      },
    ],
    [t]
  )

  // 테넌트 로고 URL (API 값 또는 기본 로고)
  const tenantId = getTenantId()
  const logoUrl = getTenantLogoUrl(tenantId, tenantInfo?.logoUrl)

  // 페이지 타이틀 자동 업데이트
  usePageTitle()

  useEffect(() => {
    if (prefersCollapsedSidebar(location.pathname)) {
      setSidebarCollapsed(true)
    }
  }, [location.pathname])

  const handleLogout = () => {
    if (mobileSidebarOpen) setSidebarCollapsed(true)
    requestCoachingExit(() => {
      logout()
      navigate('/admin/login')
    })
  }

  const renderLink = (item: SidebarItem, collapsed = sidebarCollapsed) => {
    if (!item.path) return null
    const Icon = item.icon
    const active = isActive(item)

    return (
      <Link
        aria-current={active ? 'page' : undefined}
        key={item.path}
        to={item.path}
        onClick={() => { if (mobileSidebarOpen) setSidebarCollapsed(true) }}
        title={collapsed ? item.label : undefined}
        className={clsx(
          'flex items-center rounded-lg text-sm font-medium transition-colors',
          collapsed ? 'px-3 py-2.5 justify-center' : 'px-3 py-2.5',
          active
            ? 'bg-primary-50 text-primary-600'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        )}
      >
        <Icon className={clsx(
          'w-5 h-5',
          !collapsed && 'mr-3',
          active ? 'text-primary-600' : 'text-gray-400'
        )} />
        {!collapsed && (
          <>
            {item.label}
            {item.preview && <PreviewBadge />}
            {active && (
              <ChevronRight className="w-4 h-4 ml-auto text-primary-600" />
            )}
          </>
        )}
      </Link>
    )
  }

  const testCenter = tenantInfo?.lcOperationMode === 'TEST'
  const isActive = (item: SidebarItem) => {
    if (!item.path) return false
    // TEST centers see their own menu entry for the new workspace; elsewhere it stays under the existing Today's Class entry.
    if (item.path === '/admin/coaching' && newCoachingRoute && !testCenter) return true
    if (item.exact) {
      return location.pathname === item.path
    }
    const base = item.activeBase ?? item.path
    return location.pathname === base || location.pathname.startsWith(`${base}/`)
  }

  const centerName = tenantInfo?.displayName || tenantInfo?.name || t('adminTopBar.defaultCenterName')

  const isItemAllowed = (item: SidebarItem): boolean => {
    if (item.devOnly && !isDevEnv) return false
    if (item.mallOnly && tenantInfo?.lcMallEnabled === false) return false
    // Menu visibility follows the tenant info like the mall flag; the route itself re-checks with the authenticated capability API.
    if (item.testCenterOnly && !testCenter) return false
    if (item.ownerOnly) return isLcOwner(user ?? undefined)
    if (item.anyPermission) return item.anyPermission.some((p) => hasLcPermission(user ?? undefined, p))
    if (item.permission) return hasLcPermission(user ?? undefined, item.permission)
    return true
  }

  const visibleItems = sidebarItems
    .map((item) => (item.children ? { ...item, children: item.children.filter(isItemAllowed) } : item))
    .filter((item) => (item.children ? item.children.length > 0 : isItemAllowed(item)))

  // 아코디언 하위 메뉴 포함 평탄화 (active 매칭/헤더 라벨용)
  const flatItems = visibleItems.flatMap((item) => (item.children ? item.children : [item]))

  // 현재 경로에 해당하는 사이드바 메뉴 라벨 찾기
  const getCurrentMenuLabel = () => {
    if (altCoachingRoute) return t('adminMenu.coachingAlt')

    // 정확히 일치하는 항목 먼저 찾기
    const exactMatch = flatItems.find(item => item.exact && location.pathname === item.path)
    if (exactMatch) return exactMatch.label

    // prefix 매칭 (가장 긴 경로 우선)
    const prefixMatches = flatItems
      .filter(item => !item.exact && isActive(item))
      .sort((a, b) => (b.activeBase ?? b.path!).length - (a.activeBase ?? a.path!).length)

    return prefixMatches[0]?.label || null
  }

  // 헤더에 표시할 제목: pageSubtitle > 현재 메뉴 라벨
  const headerTitle = pageSubtitle || getCurrentMenuLabel()

  // 그룹(아코디언)의 하위 항목 중 현재 경로와 매칭되는 항목 존재 여부
  const isGroupActive = (item: SidebarItem) =>
    (item.children ?? []).some((c) => c.path && location.pathname.startsWith(c.activeBase ?? c.path))

  const renderSidebar = (collapsed: boolean, overlay = false) => (
    <aside data-testid={overlay ? "admin-mobile-sidebar" : "admin-sidebar"} className={clsx(
        "fixed inset-y-0 left-0 bg-white shadow-lg pt-12 transition-all duration-300 flex flex-col",
        collapsed ? "w-16" : "w-64"
      )}>
        {/* Logo Section */}
        <div className={clsx("h-16 flex items-center border-b bg-gray-50 flex-shrink-0", newCoachingRoute ? "px-2" : "px-3")}>
          <button
            ref={overlay ? mobileSidebarToggleRef : sidebarToggleRef}
            onClick={() => setSidebarCollapsed(!collapsed)}
            className={clsx("p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors", newCoachingRoute && "min-h-11 min-w-11")}
            title={collapsed ? t('adminTopBar.sidebarExpand') : t('adminTopBar.sidebarCollapse')}
          >
            {collapsed ? (
              <PanelLeft className="w-5 h-5" />
            ) : (
              <PanelLeftClose className="w-5 h-5" />
            )}
          </button>
          {!collapsed && (
            <p className="font-semibold text-gray-900 ml-2">{t('adminMenu.consoleTitle')}</p>
          )}
        </div>

        {/* External links (dev environment only) */}
        {isDevEnv && (
          <div className="px-2 py-3 border-b border-gray-200 flex-shrink-0">
            <a
              href="http://localhost:3001"
              target="_blank"
              rel="noopener noreferrer"
              className={clsx(
                'flex items-center rounded-lg text-sm font-medium transition-colors',
                collapsed ? 'px-3 py-2 justify-center' : 'px-3 py-2',
                'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
              )}
              title="Super Admin"
            >
              <ExternalLink className={clsx('w-5 h-5', !collapsed && 'mr-3')} />
              {!collapsed && <span>Super Admin</span>}
            </a>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto mt-6 px-2">
          <div className="space-y-1">
            {visibleItems.map((item) => {
                if (item.children && item.key) {
                  const groupKey = item.key
                  const open = openGroups[groupKey] ?? isGroupActive(item)
                  const Icon = item.icon
                  return (
                    <div key={groupKey}>
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => {
                          if (collapsed) {
                            // 접힌 상태에선 하위 메뉴를 렌더하지 않으므로, 펼치면서 그룹을 연다
                            setSidebarCollapsed(false)
                            setOpenGroups((g) => ({ ...g, [groupKey]: true }))
                          } else {
                            setOpenGroups((g) => ({ ...g, [groupKey]: !open }))
                          }
                        }}
                        title={collapsed ? item.label : undefined}
                        className={clsx(
                          'w-full flex items-center rounded-lg text-sm font-medium transition-colors',
                          collapsed ? 'px-3 py-2.5 justify-center' : 'px-3 py-2.5',
                          isGroupActive(item) ? 'text-primary-600' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        )}
                      >
                        <Icon className={clsx(
                          'w-5 h-5',
                          !collapsed && 'mr-3',
                          isGroupActive(item) ? 'text-primary-600' : 'text-gray-400'
                        )} />
                        {!collapsed && (
                          <>
                            {item.label}
                            <ChevronDown className={clsx('w-4 h-4 ml-auto transition-transform', open && 'rotate-180')} />
                          </>
                        )}
                      </button>
                      {open && !collapsed && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.children.map((child) => renderLink(child, collapsed))}
                        </div>
                      )}
                    </div>
                  )
                }
                return renderLink(item, collapsed)
              })}
          </div>
        </nav>

        {/* User Info */}
        <div className={clsx("border-t bg-gray-50 flex-shrink-0", newCoachingRoute ? "p-2" : "p-3")}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <Link
                to="/admin/profile"
                onClick={() => { if (mobileSidebarOpen) setSidebarCollapsed(true) }}
                className={clsx("w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center hover:bg-primary-200 transition-colors", newCoachingRoute && "min-h-11 min-w-11")}
                title={t('adminTopBar.myPage')}
              >
                <span className="text-sm font-medium text-primary-600">
                  {user?.fullName?.charAt(0) || 'U'}
                </span>
              </Link>
              <button
                onClick={handleLogout}
                className={clsx("p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100", newCoachingRoute && "min-h-11 min-w-11")}
                title={t('adminTopBar.logout')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <Link to="/admin/profile" onClick={() => { if (mobileSidebarOpen) setSidebarCollapsed(true) }} className="flex items-center min-w-0 hover:opacity-80 transition-opacity">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-600">
                    {user?.fullName?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="ml-3 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.fullName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.email}
                  </p>
                </div>
              </Link>
              <button
                onClick={handleLogout}
                className={clsx("p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100", newCoachingRoute && "min-h-11 min-w-11")}
                title={t('adminTopBar.logout')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </aside>
  )

  return (
    <div className={coachingRoute ? "min-h-screen bg-muted" : "min-h-screen bg-gray-100"} style={shortcutSpacing}>
      {/* Top Header Bar - displays company name */}
      <header
        className={clsx('fixed top-0 left-0 right-0 h-12 z-50 flex items-center', altCoachingRoute ? 'gap-2 px-3 text-white sm:px-4 lg:px-6' : newCoachingRoute ? 'gap-2 px-3 sm:px-4 lg:px-6' : 'px-6')}
        style={{ backgroundColor: tenantInfo?.primaryColor || '#6366f1' }}
      >
        {altCoachingRoute ? <Button variant="ghost" size="icon" className="size-11 shrink-0" aria-label={t('adminTopBar.sidebarExpand')} aria-expanded={mobileNavigationOpen} aria-haspopup="dialog" onClick={() => setMobileNavigationOpen(true)}><Menu /></Button> : null}
        <div className={altCoachingRoute || newCoachingRoute ? 'flex min-w-0 flex-1 items-center gap-3' : 'flex items-center space-x-3'}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={tenantInfo?.name || 'Quebon'}
              className={clsx("h-7 w-auto", newCoachingRoute && "shrink-0")}
              onError={(e) => {
                // 로고 로딩 실패 시 기본 로고로 대체
                (e.target as HTMLImageElement).src = '/quebon-logo.svg'
              }}
            />
          ) : (
            <div className={clsx("h-7 w-7 rounded bg-white/20 flex items-center justify-center text-white font-bold text-sm", newCoachingRoute && "shrink-0")}>
              {tenantInfo?.name?.charAt(0) || 'Q'}
            </div>
          )}
          <span className={clsx('font-bold text-white text-lg', (altCoachingRoute || newCoachingRoute) && 'min-w-0 truncate')}>
            {centerName}
          </span>
          {headerTitle && (
            <>
              <span className={clsx("text-white/50", (altCoachingRoute || newCoachingRoute) && "hidden sm:inline")}>|</span>
              <span className={clsx("text-white font-medium", (altCoachingRoute || newCoachingRoute) && "hidden min-w-0 truncate sm:inline")}>{headerTitle}</span>
            </>
          )}
        </div>
        <div className={altCoachingRoute || newCoachingRoute ? "ml-auto flex shrink-0 items-center gap-2" : "ml-auto flex items-center space-x-4"}>
          <LanguageSwitcher />
          <div className={altCoachingRoute ? "hidden items-center gap-2 text-white/90 lg:flex" : "flex items-center space-x-2 text-white/90"}>
            <Shield className="w-4 h-4" />
            <span className="text-sm">{t('adminTopBar.adminBadge')}</span>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      {!altCoachingRoute ? renderSidebar(mobileSidebarOpen ? true : sidebarCollapsed) : null}
      {newCoachingRoute ? <Dialog open={mobileSidebarOpen} onOpenChange={open => { if (!open) setSidebarCollapsed(true) }}>
        <DialogPortal>
          <DialogOverlay data-testid="admin-mobile-sidebar-backdrop" render={<button type="button" tabIndex={-1} aria-label={t('adminTopBar.sidebarCollapse')} />} onClick={() => setSidebarCollapsed(true)} />
        </DialogPortal>
        <DialogContent data-testid="admin-mobile-sidebar-dialog" showCloseButton={false} overlayClassName="hidden"
          initialFocus={() => mobileSidebarToggleRef.current} finalFocus={() => sidebarToggleRef.current}
          className="inset-y-0 left-0 top-0 h-dvh max-h-dvh w-64 max-w-64 translate-x-0 translate-y-0 gap-0 overflow-visible p-0 sm:max-w-64">
          <DialogTitle className="sr-only">{t('adminMenu.consoleTitle')}</DialogTitle>
          {renderSidebar(false, true)}
        </DialogContent>
      </Dialog> : null}

      {altCoachingRoute ? <Dialog open={mobileNavigationOpen} onOpenChange={setMobileNavigationOpen}>
        <DialogContent showCloseButton={false} className="max-h-[calc(100dvh-2rem)] overflow-y-auto">
          <DialogHeader><DialogTitle>{t('adminMenu.consoleTitle')}</DialogTitle></DialogHeader>
          <nav aria-label={t('adminMenu.consoleTitle')} className="flex flex-col gap-1">
            {flatItems.map(item => item.path ? <Link key={item.path} to={item.path}
              onClick={() => setMobileNavigationOpen(false)}
              aria-current={isActive(item) ? 'page' : undefined}
              className={clsx('flex min-h-11 items-center gap-3 rounded-lg px-3 py-2', isActive(item) ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-muted')}>
              <item.icon className="size-5 shrink-0" aria-hidden="true" />{item.label}
            </Link> : null)}
            <Link to="/admin/profile" onClick={() => setMobileNavigationOpen(false)} className="flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-foreground hover:bg-muted"><UserCog className="size-5 shrink-0" aria-hidden="true" />{t('adminTopBar.myPage')}</Link>
          </nav>
          <div className="flex flex-col gap-2 border-t border-border pt-3">
            <Button variant="outline" className="min-h-11" onClick={() => setMobileNavigationOpen(false)}>{t('adminTopBar.sidebarCollapse')}</Button>
            <Button variant="ghost" className="min-h-11" onClick={() => { setMobileNavigationOpen(false); handleLogout() }}><LogOut data-icon="inline-start" />{t('adminTopBar.logout')}</Button>
          </div>
        </DialogContent>
      </Dialog> : null}

      {/* Main Content */}
      <div data-testid="admin-main-gutter" className={clsx(
        coachingRoute ? "min-w-0 pt-12" : "pt-12",
        newCoachingRoute ? "transition-[padding-left] duration-300 motion-reduce:transition-none" : "transition-all duration-300",
        altCoachingRoute ? "pl-0" : (sidebarCollapsed || mobileSidebarOpen ? "pl-16" : "pl-64")
      )}>
        <main className={coachingRoute ? "min-w-0" : "p-8"}>
          <HandheldCoachingSelectionContext.Provider value={setHandheldSelection}>
            <Outlet />
          </HandheldCoachingSelectionContext.Provider>
          {showGradingShortcut ? <HandheldCoachingShortcut selection={legacyCoachingRoute ? handheldSelection ?? undefined : undefined} /> : null}
        </main>
      </div>
    </div>
  )
}
